import { EditorContextProvider } from '@/components/editor/EditorContext';
import { TemplatePicker } from '@/components/editor/TemplatePicker';
import { TEMPLATES, getTemplate } from '@/components/templates/registry';
import { ANTIPLATELET_SAMPLE_FICHE } from '@/lib/sampleData';
import type { TemplateId } from '@pharmafiche/schema';
import { useState } from 'react';

/**
 * /sample exists so the visual baseline tests can confirm the antiplatelet
 * sample renders correctly in each template. A top bar lets you flip between
 * the 6 templates; the fiche JSON stays identical.
 */
export function SampleRoute() {
  const [templateId, setTemplateId] = useState<TemplateId>('dessinee');
  const [pickerOpen, setPickerOpen] = useState(false);
  const fiche = {
    ...ANTIPLATELET_SAMPLE_FICHE,
    meta: { ...ANTIPLATELET_SAMPLE_FICHE.meta, template_id: templateId },
  };
  const template = getTemplate(templateId);
  const Component = template.Component;

  return (
    <EditorContextProvider mode="read">
      <div className="bg-ink text-paper py-2 px-4 flex items-center justify-between font-hand text-[14px] gap-3 flex-wrap">
        <a href="/" className="hover:underline underline-offset-4">
          ← Retour
        </a>
        <div className="flex items-center gap-1 flex-wrap">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTemplateId(t.id)}
              className={[
                'px-3 py-1 rounded-control border-[2px] text-[13px] transition-colors',
                t.id === templateId
                  ? 'bg-accent-yellow text-ink border-paper'
                  : 'bg-transparent text-paper border-paper/30 hover:bg-paper/10',
              ].join(' ')}
              data-testid={`sample-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="px-3 py-1 rounded-control border-[2px] bg-transparent text-paper border-paper/30 hover:bg-paper/10 text-[13px]"
          >
            🎨 Picker
          </button>
        </div>
        <span className="font-mono text-[12px] opacity-60">{templateId}</span>
      </div>
      <main data-testid="sample-root">
        <Component fiche={fiche} />
      </main>
      {pickerOpen ? (
        <TemplatePicker
          fiche={fiche}
          currentId={templateId}
          onPick={(id) => {
            setTemplateId(id);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </EditorContextProvider>
  );
}
